import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  CircleHelp,
  Info,
  MessageSquareText,
  XCircle,
} from 'lucide-react';

import { cn } from '@/lib/shadcn/utils';
import type {
  CandidateAssessmentAvailabilityApi,
  CandidateLedgerAssessmentApi,
  CandidatePrescreenContextApi,
} from '@/modules/candidates/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/ui/components/popover/Popover';

type LedgerCriterion = CandidateLedgerAssessmentApi['criteria'][number];

export type LedgerAssessmentView = CandidateLedgerAssessmentApi;

const availabilityCopy: Record<
  CandidateAssessmentAvailabilityApi,
  { label: string; detail: string; className: string }
> = {
  ready: {
    label: 'Current',
    detail: 'This assessment matches the current job requirements.',
    className: 'border-green-200 bg-green-50 text-green-800',
  },
  pending: {
    label: 'Updating',
    detail: 'A new assessment is being prepared.',
    className: 'border-blue-200 bg-blue-50 text-blue-800',
  },
  failed: {
    label: 'Update failed',
    detail: 'The latest assessment could not be generated.',
    className: 'border-red-200 bg-red-50 text-red-800',
  },
  stale: {
    label: 'Previous criteria',
    detail: 'This assessment predates the current job requirements.',
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  unavailable: {
    label: 'Not available',
    detail: 'No assessment is available yet.',
    className: 'border-neutral-200 bg-neutral-50 text-neutral-700',
  },
};

const statusStyle: Record<
  string,
  { icon: typeof CheckCircle2; className: string }
> = {
  supported: {
    icon: CheckCircle2,
    className: 'border-green-200 bg-green-50 text-green-800',
  },
  partial: {
    icon: CircleDashed,
    className: 'border-blue-200 bg-blue-50 text-blue-800',
  },
  unresolved: {
    icon: CircleHelp,
    className: 'border-amber-200 bg-amber-50 text-amber-800',
  },
  conflicting: {
    icon: AlertTriangle,
    className: 'border-orange-200 bg-orange-50 text-orange-800',
  },
  contradicted: {
    icon: XCircle,
    className: 'border-red-200 bg-red-50 text-red-800',
  },
};

const groupLabel: Record<string, string> = {
  supported: 'Met',
  unresolved: 'Not confirmed yet',
};

function humanize(value: string) {
  return value
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function provenanceLabel(
  sourceKind: string | null | undefined,
  evidenceBasis: string | null | undefined,
) {
  const labels: string[] = [];
  const isCandidateReported =
    evidenceBasis === 'candidate_self_report' ||
    evidenceBasis === 'candidate_statement' ||
    evidenceBasis === 'indirect_call_record' ||
    sourceKind === 'raw_note' ||
    sourceKind === 'prescreen_transcript';
  if (isCandidateReported) {
    labels.push('Candidate self-report', 'Not independently verified');
  } else if (evidenceBasis) {
    labels.push(humanize(evidenceBasis));
  }
  if (sourceKind) labels.push(humanize(sourceKind));
  return labels.join(' · ');
}

function AssessmentAvailabilityBadge({
  availability,
}: {
  availability: CandidateAssessmentAvailabilityApi;
}) {
  const copy = availabilityCopy[availability];
  return (
    <Badge
      variant="outline"
      size="sm"
      className={copy.className}
      title={copy.detail}
    >
      {copy.label}
    </Badge>
  );
}

function MetricLabel({
  label,
  explanation,
}: {
  label: string;
  explanation: string;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <p className="text-[11px] font-medium tracking-wide text-neutral-500 uppercase">
        {label}
      </p>
      <Popover>
        <PopoverTrigger
          aria-label={`What is ${label.toLowerCase()}?`}
          className="relative rounded-full text-neutral-400 after:absolute after:-inset-3 hover:text-neutral-600 focus-visible:ring-2 focus-visible:ring-neutral-300 focus-visible:outline-none"
        >
          <Info className="size-3.5" aria-hidden />
        </PopoverTrigger>
        <PopoverContent
          align="end"
          collisionPadding={16}
          className="w-64 p-3 text-sm leading-5 text-neutral-700"
        >
          {explanation}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function CriterionRow({ criterion }: { criterion: LedgerCriterion }) {
  const style = statusStyle[criterion.status] ?? {
    icon: CircleHelp,
    className: 'border-neutral-200 bg-neutral-50 text-neutral-700',
  };
  const StatusIcon = style.icon;

  return (
    <li className="border-b border-neutral-200 py-4 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900">
            {criterion.requirement}
          </p>
          <p className="mt-1 text-xs font-medium tracking-wide text-neutral-500 uppercase">
            {humanize(criterion.importance)}
          </p>
        </div>
        <Badge variant="outline" size="sm" className={style.className}>
          <StatusIcon aria-hidden />
          {humanize(criterion.status)}
        </Badge>
      </div>

      {criterion.explanation && (
        <p className="mt-3 text-sm leading-6 text-neutral-700">
          {criterion.explanation}
        </p>
      )}

      {criterion.evidence.length > 0 && (
        <div className="mt-3 space-y-2">
          {criterion.evidence.map((item, index) => (
            <blockquote
              key={`${item.quote}-${index}`}
              className="border-l-2 border-neutral-300 pl-3 text-sm leading-5 text-neutral-700"
            >
              “{item.quote}”
              <footer className="mt-1 text-xs text-neutral-500">
                {provenanceLabel(item.sourceKind, item.evidenceBasis)}
              </footer>
            </blockquote>
          ))}
        </div>
      )}

      {criterion.status !== 'supported' && criterion.question && (
        <div className="mt-3 flex gap-2 rounded-md bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
          <MessageSquareText className="mt-0.5 size-4 shrink-0 text-neutral-500" />
          <span>{criterion.question}</span>
        </div>
      )}
    </li>
  );
}

interface CandidateLedgerAssessmentProps {
  assessment: LedgerAssessmentView;
  title?: string;
  description?: string;
  className?: string;
  compact?: boolean;
  showAvailabilityBadge?: boolean;
  showMetrics?: boolean;
}

export function CandidateLedgerAssessment({
  assessment,
  title = 'Candidate assessment',
  description,
  className,
  compact = false,
  showAvailabilityBadge = true,
  showMetrics = true,
}: CandidateLedgerAssessmentProps) {
  const copy = availabilityCopy[assessment.availability];
  const hasAssessment =
    Boolean(assessment.narrativeSummary) || assessment.criteria.length > 0;
  const visibleCriteria = compact
    ? assessment.criteria.slice(0, 4)
    : assessment.criteria;
  const statusOrder = [
    'contradicted',
    'conflicting',
    'supported',
    'partial',
    'unresolved',
  ];
  const criterionGroups = [
    ...statusOrder.map((status) => ({
      status,
      criteria: visibleCriteria.filter(
        (criterion) => criterion.status === status,
      ),
    })),
    {
      status: 'other',
      criteria: visibleCriteria.filter(
        (criterion) => !statusOrder.includes(criterion.status),
      ),
    },
  ].filter((group) => group.criteria.length > 0);

  return (
    <section
      className={cn(
        'overflow-hidden rounded-xl border border-neutral-200 bg-white',
        className,
      )}
    >
      <header className="border-b border-neutral-200 bg-neutral-50/70 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-neutral-950">{title}</h3>
              {showAvailabilityBadge && (
                <AssessmentAvailabilityBadge
                  availability={assessment.availability}
                />
              )}
            </div>
            {description && (
              <p className="mt-1 text-sm text-neutral-600">{description}</p>
            )}
          </div>
          {showMetrics && (
            <div className="flex gap-5 text-right">
              <div>
                <MetricLabel
                  label="Score"
                  explanation="How well the candidate matches the job's requirements."
                />
                <p className="text-xl font-semibold text-neutral-950 tabular-nums">
                  {assessment.score === null
                    ? '—'
                    : assessment.score.toFixed(1)}
                </p>
              </div>
              <div>
                <MetricLabel
                  label="Coverage"
                  explanation="How many requirements we could check so far, met or not."
                />
                <p className="text-xl font-semibold text-neutral-950 tabular-nums">
                  {assessment.coveragePercent === null
                    ? '—'
                    : `${assessment.coveragePercent.toFixed(0)}%`}
                </p>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="px-5 py-4">
        {!hasAssessment ? (
          <p className="text-sm text-neutral-600">{copy.detail}</p>
        ) : (
          <div className="space-y-5">
            {assessment.narrativeSummary && (
              <p className="max-w-[76ch] text-sm leading-6 text-neutral-700">
                {assessment.narrativeSummary}
              </p>
            )}

            {assessment.call && (
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
                <p className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                  Pre-screen call: {humanize(assessment.call.status)}
                </p>
                {assessment.call.explanation && (
                  <p className="mt-1 text-sm leading-5 text-neutral-700">
                    {assessment.call.explanation}
                  </p>
                )}
              </div>
            )}

            {assessment.criteria.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-neutral-900">
                  Requirements match
                </h4>
                <div className="mt-3 space-y-5">
                  {criterionGroups.map((group) => (
                    <section key={group.status}>
                      <h5 className="text-xs font-semibold tracking-wide text-neutral-500 uppercase">
                        {groupLabel[group.status] ?? humanize(group.status)} (
                        {group.criteria.length})
                      </h5>
                      <ul>
                        {group.criteria.map((criterion) => (
                          <CriterionRow
                            key={criterion.requirementId}
                            criterion={criterion}
                          />
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
                {compact && assessment.criteria.length > 4 && (
                  <p className="pt-2 text-xs text-neutral-500">
                    {assessment.criteria.length - 4} more requirements in the
                    full assessment
                  </p>
                )}
              </div>
            )}

            {!compact && assessment.facts.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-neutral-900">
                  Candidate facts
                </h4>
                <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                  {assessment.facts.map((fact, index) => (
                    <div
                      key={`${fact.kind}-${index}`}
                      className="rounded-lg bg-neutral-50 px-3 py-2"
                    >
                      <dt className="text-xs font-medium text-neutral-500">
                        {humanize(fact.kind)}
                      </dt>
                      <dd className="mt-1 text-sm text-neutral-800">
                        {fact.rawText}
                      </dd>
                      <dd className="mt-1 text-xs text-neutral-500">
                        {provenanceLabel(fact.sourceKind, fact.evidenceBasis)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {!compact &&
              assessment.changes &&
              assessment.changes.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-neutral-900">
                    Changed after the pre-screen
                  </h4>
                  <ul className="mt-3 space-y-3">
                    {assessment.changes.map((change) => (
                      <li
                        key={change.requirementId}
                        className="rounded-lg border border-neutral-200 px-4 py-3"
                      >
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <Badge variant="outline" size="sm">
                            {humanize(change.from)}
                          </Badge>
                          <span aria-hidden className="text-neutral-400">
                            →
                          </span>
                          <Badge variant="outline" size="sm">
                            {humanize(change.to)}
                          </Badge>
                        </div>
                        {change.addedEvidence.map((evidence, index) => (
                          <blockquote
                            key={`${evidence.quote}-${index}`}
                            className="mt-2 border-l-2 border-neutral-300 pl-3 text-sm leading-5 text-neutral-700"
                          >
                            “{evidence.quote}”
                            <footer className="mt-1 text-xs text-neutral-500">
                              {provenanceLabel(
                                evidence.sourceKind,
                                evidence.evidenceBasis,
                              )}
                            </footer>
                          </blockquote>
                        ))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {!compact && assessment.decision && (
              <div className="border-t border-neutral-200 pt-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="text-sm font-semibold text-neutral-900">
                    Assessment decision
                  </h4>
                  <Badge variant="outline" size="sm">
                    {humanize(assessment.decision.outcome)}
                  </Badge>
                </div>
                {assessment.decision.reasoning && (
                  <p className="mt-2 text-sm leading-6 text-neutral-700">
                    {assessment.decision.reasoning}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export function LedgerRevisionComparisonNotice({
  current,
  atSubmission,
  showScore = true,
}: {
  current: LedgerAssessmentView;
  atSubmission: LedgerAssessmentView;
  showScore?: boolean;
}) {
  if (
    current.revisionId === atSubmission.revisionId ||
    !current.revisionId ||
    !atSubmission.revisionId
  ) {
    return null;
  }

  const requirementComparisonKnown = Boolean(
    current.requirementRevisionId && atSubmission.requirementRevisionId,
  );
  const requirementsChanged =
    requirementComparisonKnown &&
    current.requirementRevisionId !== atSubmission.requirementRevisionId;
  let explanation = requirementsChanged
    ? 'The job requirements changed after submission, so the current assessment and the submitted snapshot use different criteria.'
    : requirementComparisonKnown
      ? 'The assessment was refreshed after submission using newer evidence against the same job requirements.'
      : 'The assessment was refreshed after submission; the assessment recorded at submission remains available for comparison.';
  if (
    showScore &&
    current.score !== null &&
    atSubmission.score !== null &&
    current.score !== atSubmission.score
  ) {
    const reason = requirementsChanged
      ? 'the current assessment uses updated job requirements'
      : requirementComparisonKnown
        ? 'the assessment was refreshed with newer evidence'
        : 'the current assessment is a newer revision';
    explanation = `The current score is ${current.score.toFixed(1)}, compared with ${atSubmission.score.toFixed(1)} at submission, because ${reason}.`;
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-5 text-amber-950">
      {explanation} The candidate’s submitted status and the assessment recorded
      at submission have not changed.
    </div>
  );
}

type QuestionSelection =
  CandidatePrescreenContextApi['preparedQuestionSelection'];
type PrescreenAttempt = CandidatePrescreenContextApi['latestAttempt'];

export interface PrescreenContextView {
  questionSelectionAvailability: CandidateAssessmentAvailabilityApi;
  preparedQuestionSelection: QuestionSelection;
  conversationQuestionSelection: QuestionSelection;
  latestAttempt: PrescreenAttempt;
}

function QuestionSet({
  title,
  questions,
  emptyText,
}: {
  title: string;
  questions: QuestionSelection;
  emptyText: string;
}) {
  return (
    <section>
      <h4 className="text-sm font-semibold text-neutral-900">{title}</h4>
      {questions?.questions?.length ? (
        <ol className="mt-2 space-y-2">
          {questions.questions.map((question, index) => (
            <li
              key={question.questionId}
              className="flex gap-3 rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-700"
            >
              <span className="text-neutral-400 tabular-nums">{index + 1}</span>
              <span>
                <span className="block">{question.text}</span>
                <span className="mt-0.5 block text-xs text-neutral-500">
                  {humanize(question.source)}
                  {question.requirementIds.length > 0 &&
                    ` · ${question.requirementIds.length} linked ${question.requirementIds.length === 1 ? 'requirement' : 'requirements'}`}
                </span>
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-2 text-sm text-neutral-600">{emptyText}</p>
      )}
      {questions?.rationale && (
        <p className="mt-2 text-xs leading-5 text-neutral-500">
          {questions.rationale}
        </p>
      )}
    </section>
  );
}

export function CandidatePrescreenContext({
  context,
}: {
  context: PrescreenContextView;
}) {
  return (
    <div className="space-y-5 rounded-xl border border-neutral-200 bg-white p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-neutral-950">Pre-screen context</h3>
        <AssessmentAvailabilityBadge
          availability={context.questionSelectionAvailability}
        />
      </div>

      <QuestionSet
        title="Prepared for the next call"
        questions={context.preparedQuestionSelection}
        emptyText="No current question set is ready."
      />
      <QuestionSet
        title="Saved for the latest call"
        questions={context.conversationQuestionSelection}
        emptyText="No completed or started call has a saved question set yet."
      />

      {context.latestAttempt && (
        <section className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-neutral-900">
              Latest call attempt
            </h4>
            <Badge variant="outline" size="sm">
              {humanize(context.latestAttempt.outcome)}
            </Badge>
          </div>
          {context.latestAttempt.reason && (
            <p className="mt-2 text-sm leading-5 text-neutral-700">
              {context.latestAttempt.reason}
            </p>
          )}
          {(context.latestAttempt.completedAt ??
            context.latestAttempt.startedAt) && (
            <p className="mt-2 text-xs text-neutral-500">
              {context.latestAttempt.completedAt ? 'Completed' : 'Started'}{' '}
              {new Intl.DateTimeFormat(undefined, {
                dateStyle: 'medium',
                timeStyle: 'short',
              }).format(
                new Date(
                  context.latestAttempt.completedAt ??
                    context.latestAttempt.startedAt!,
                ),
              )}
            </p>
          )}
        </section>
      )}
    </div>
  );
}

export function CandidateAssessmentSummaryBadge({
  assessment,
}: {
  assessment: {
    mode: 'legacy' | 'ledger';
    displayedAvailability: CandidateAssessmentAvailabilityApi | null;
    displayedCoveragePercent: number | null;
  };
}) {
  if (assessment.mode !== 'ledger') return null;
  const availability = assessment.displayedAvailability ?? 'unavailable';
  const coverage = assessment.displayedCoveragePercent;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <AssessmentAvailabilityBadge availability={availability} />
      {coverage !== null && (
        <span className="text-xs text-neutral-500 tabular-nums">
          {coverage.toFixed(0)}% coverage
        </span>
      )}
    </div>
  );
}
